import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workflowService } from "@/lib/api/services/workflow.service";
import { workflowKeys } from "../keys/workflows.keys";
import type {
  CreateWorkflowPayload,
  CreateWorkflowWebhookPayload,
  UpdateWorkflowPayload,
  UpdateWorkflowDefinitionPayload,
  UpdateWorkflowWebhookPayload,
} from "@/types/workflow.types";

// ---------------------------------------------------------------------------
// Checkpoint mutations
// ---------------------------------------------------------------------------

/** Approve a checkpoint — first approver sends formData; subsequent approvers
 *  send formData: null (form locked server-side per multi-approver invariant). */
export function useApproveCheckpoint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      formData,
      comment,
    }: {
      id: string;
      formData?: Record<string, unknown> | null;
      comment?: string;
    }) => workflowService.approveCheckpoint(id, { formData, comment }),
    onSuccess: (data, { id }) => {
      const checkpoint = data.data;
      queryClient.invalidateQueries({ queryKey: workflowKeys.checkpointDetail(id) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.checkpoints() });
      if (checkpoint?.runId) {
        queryClient.invalidateQueries({ queryKey: workflowKeys.runCheckpoints(checkpoint.runId) });
        queryClient.invalidateQueries({ queryKey: workflowKeys.run(checkpoint.runId) });
      }
    },
  });
}

/** Reject a checkpoint — one reject short-circuits quorum regardless of
 *  remaining pending approvers, mirroring BE quorum logic. */
export function useRejectCheckpoint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) =>
      workflowService.rejectCheckpoint(id, { comment }),
    onSuccess: (data, { id }) => {
      const checkpoint = data.data;
      queryClient.invalidateQueries({ queryKey: workflowKeys.checkpointDetail(id) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.checkpoints() });
      if (checkpoint?.runId) {
        queryClient.invalidateQueries({ queryKey: workflowKeys.runCheckpoints(checkpoint.runId) });
        queryClient.invalidateQueries({ queryKey: workflowKeys.run(checkpoint.runId) });
      }
    },
  });
}

/** Cancel a checkpoint — requires workflow.edit ACL on parent workflow. */
export function useCancelCheckpoint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      workflowService.cancelCheckpoint(id, { reason }),
    onSuccess: (data, { id }) => {
      const checkpoint = data.data;
      queryClient.invalidateQueries({ queryKey: workflowKeys.checkpointDetail(id) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.checkpoints() });
      if (checkpoint?.runId) {
        queryClient.invalidateQueries({ queryKey: workflowKeys.runCheckpoints(checkpoint.runId) });
        queryClient.invalidateQueries({ queryKey: workflowKeys.run(checkpoint.runId) });
      }
    },
  });
}

/** Create a new workflow */
export function useCreateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateWorkflowPayload) => workflowService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

/** Update an existing workflow */
export function useUpdateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWorkflowPayload }) =>
      workflowService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

/** Delete a workflow */
export function useDeleteWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => workflowService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

/** Trigger a workflow run */
export function useRunWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => workflowService.run(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: [...workflowKeys.detail(id), "runs"] });
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

/**
 * PATCH workflow definition — used for step uiPosition drag-persist.
 * Does not invalidate the full list; only refreshes the detail + steps cache
 * for the affected workflow so the canvas reflects the saved positions.
 */
export function useUpdateWorkflowDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, definition }: { id: string } & UpdateWorkflowDefinitionPayload) =>
      workflowService.updateDefinition(id, { definition }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.steps(id) });
    },
  });
}

/**
 * PATCH a step's config. Optimistic invalidate refreshes node summary
 * inline; on error revert via cached snapshot. Avoids spinner thrash on
 * fast networks.
 */
export function useUpdateStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workflowId,
      stepId,
      config,
      connectorId,
    }: {
      workflowId: string;
      stepId: string;
      config: Record<string, unknown>;
      connectorId?: string;
    }) => workflowService.updateStepConfig(workflowId, stepId, config, connectorId),

    onMutate: async ({ workflowId }) => {
      // Cancel any in-flight refetches so they don't overwrite optimistic state.
      await queryClient.cancelQueries({ queryKey: workflowKeys.detail(workflowId) });
      await queryClient.cancelQueries({ queryKey: workflowKeys.steps(workflowId) });
      // Snapshot for rollback.
      const snapshot = {
        detail: queryClient.getQueryData(workflowKeys.detail(workflowId)),
        steps: queryClient.getQueryData(workflowKeys.steps(workflowId)),
      };
      return { snapshot };
    },

    onError: (_err, { workflowId }, ctx) => {
      if (ctx?.snapshot) {
        queryClient.setQueryData(workflowKeys.detail(workflowId), ctx.snapshot.detail);
        queryClient.setQueryData(workflowKeys.steps(workflowId), ctx.snapshot.steps);
      }
    },

    onSettled: (_data, _err, { workflowId }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(workflowId) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.steps(workflowId) });
    },
  });
}

/** Move a workflow into a folder (or root when folderId is null). */
export function useMoveWorkflowToFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, folderId }: { workflowId: string; folderId: string | null }) =>
      workflowService.moveToFolder(workflowId, folderId),
    onSuccess: (_, { workflowId }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(workflowId) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}

/** Cancel a running workflow */
export function useCancelWorkflowRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (runId: string) => workflowService.cancelRun(runId),
    onSuccess: (_, runId) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.run(runId) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.runSteps(runId) });
    },
  });
}

/**
 * Resume a manually-paused workflow run. Triggers BE
 * POST /workflow-runs/:id/resume which enqueues a continuation onto the
 * agentic-runner outbox. UI surfaces the button only on
 * status='paused' (manual pause); checkpoint pauses use the approval flow.
 */
export function useResumeWorkflowRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (runId: string) => workflowService.resumeRun(runId),
    onSuccess: (_, runId) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.run(runId) });
      queryClient.invalidateQueries({ queryKey: workflowKeys.runSteps(runId) });
    },
  });
}

/**
 * Retry a failed or cancelled workflow run. Backend creates a NEW run
 * row that reruns the entire workflow with the same configuration; the
 * source run stays as an audit fact. Returns the new run id so the
 * caller can navigate to it.
 *
 * Idempotency-Key auto-generated client-side so a rapid double-click
 * deduplicates BE-side (5min TTL). Tab reload generates a new key —
 * intentional so reload + click is treated as fresh retry intent.
 */
export function useRetryWorkflowRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (runId: string) => workflowService.retryRun(runId, crypto.randomUUID()),
    onSuccess: (_, sourceRunId) => {
      // Source run gets a `retried_to` event; refresh detail.
      queryClient.invalidateQueries({ queryKey: workflowKeys.run(sourceRunId) });
      // Predicate match — invalidate any runs list regardless of workflowId
      // so the new row appears wherever the user is browsing.
      queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) && q.queryKey[0] === "workflow" && q.queryKey.includes("runs"),
      });
    },
  });
}

export function useCreateWorkflowWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      workflowId,
      data,
    }: {
      workflowId: string;
      data: CreateWorkflowWebhookPayload;
    }) => workflowService.createWebhook(workflowId, data),
    onSuccess: (_, { workflowId }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.webhooks(workflowId) });
    },
    meta: { skipErrorToast: true },
  });
}

export function useUpdateWorkflowWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      workflowId,
      triggerId,
      data,
    }: {
      workflowId: string;
      triggerId: string;
      data: UpdateWorkflowWebhookPayload;
    }) => workflowService.updateWebhook(workflowId, triggerId, data),
    onSuccess: (_, { workflowId }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.webhooks(workflowId) });
    },
    meta: { skipErrorToast: true },
  });
}

export function useRotateWorkflowWebhookSecret() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workflowId, triggerId }: { workflowId: string; triggerId: string }) =>
      workflowService.rotateWebhookSecret(workflowId, triggerId),
    onSuccess: (_, { workflowId }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.webhooks(workflowId) });
    },
    meta: { skipErrorToast: true },
  });
}

export function useDeleteWorkflowWebhook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workflowId, triggerId }: { workflowId: string; triggerId: string }) =>
      workflowService.deleteWebhook(workflowId, triggerId),
    onSuccess: (_, { workflowId }) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.webhooks(workflowId) });
    },
    meta: { skipErrorToast: true },
  });
}
